import { BaseChartElement, isHighLowLine, isLineShape, isTrendLine, isUpDownBars } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-elements/base';
import type { Chart as G2Chart } from '@tencent/chart/lib/antv/g2';
import flatten from 'lodash/flatten';
import {
    ChartElementType,
    IChartElement,
    IChartElementGroup,
    SelectStyleType,
    SpecialGeometryType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import _groupBy from 'lodash/groupBy';

export type IGeometryElement = IChartElement;

export class Geometry extends BaseChartElement {
    public getElements(chart: G2Chart): Array<IChartElementGroup<IGeometryElement>> {
        const geometriesOfAllViews = flatten(chart.views.map(view => view.geometries));
        const g2Elements = flatten(geometriesOfAllViews.map(geometry => geometry.elements));

        const geometryElements: IGeometryElement[] = g2Elements.map((g2Element, index) => {
            const elementData = g2Element.getData();
            const data =  Array.isArray(elementData) ? elementData[0] : elementData;
            const { seriesId } = data;
            const isTrendLineElement = isTrendLine(data.specialGeometryType);

            const selectStyle = isTrendLineElement ? SelectStyleType.ENDS_ANCHOR : SelectStyleType.ANCHOR;
            const g2ElementAttr = g2Element.shape.attr();
            const elementPath = this.generatePath(g2ElementAttr);
            // @ts-ignore private属性
            const { shapeType } = g2Element;

            return {
                id: `${ChartElementType.GEOMETRY}-${index}`,
                type: ChartElementType.GEOMETRY,
                path: elementPath,
                // 图形用不到整体的rect，而且这个rect没有太大的意义。geometry在于path的路径锚点选区，这里先用一个无效的值
                rect: { left: 0, top: 0, height: 0, width: 0 },
                shapeType,
                selectStyle,
                extra: {
                    // 为什么geometry把elementData放在data下，因为elementData可能是数组（一个geometry对应多个data，比如折线图)
                    data: elementData,
                    seriesId,
                    dataId: data.dataId,
                    chartId: data.chartId,
                    specialGeometryType: data.specialGeometryType,
                },
                disableGroupSelect: isTrendLineElement,
                disableSingleSelect: isUpDownBars(data.specialGeometryType)
                || isHighLowLine(data.specialGeometryType)
                || g2ElementAttr.symbol
                || isLineShape(shapeType),
            };
        });

        // TODO 折线
        const geometryElementsGroupList: IChartElementGroup<IGeometryElement>[] = [];
        const groupedElements = _groupBy(geometryElements, (element) => {
            // 箱体图 根据箱体类型进行分组
            if (element.extra.specialGeometryType === SpecialGeometryType.upDownBar) {
                return `upDownBars_${element.extra.data.barType}`;
            }
            // 烛台图 单独分类
            if (element.extra.specialGeometryType === SpecialGeometryType.highLowLine) {
                return 'sitck';
            }
            // 趋势线和普通图形 同样拥有seriesId, 这里区分开，避免趋势线和图形分到一组
            if (element.extra.specialGeometryType === SpecialGeometryType.trendLine) {
                return `trendline_${element.extra.seriesId}`;
            }
            // 常规图形
            return element.extra.seriesId;
        });

        Object.keys(groupedElements).forEach((groupKey, index) => {
            const groupId = this.generateGroupId(groupKey);
            const elements = this.filterElement(groupedElements[groupKey]);

            geometryElementsGroupList.push({
                group: elements.map(groupElement => ({
                    ...groupElement,
                    groupId,
                })),
                type: ChartElementType.GEOMETRY,
                id: groupId,
            });
        });

        return geometryElementsGroupList;
    }

    private generateGroupId(groupKey: string) {
        return `${ChartElementType.GEOMETRY}-${groupKey}`;
    }

    private generatePath({ path, symbol, x, y, r }: any) {
        if (symbol) {
            return this.generateSymbolPath(x, y, r);
        }

        return path;
    }

    private generateSymbolPath(x: number, y: number, r: number) {
        const path = [
            ['M', x - r, y - r],
            ['L', x + r, y - r],
            ['L', x + r, y + r],
            ['L', x - r, y + r],
            ['Z'],
        ];

        return path;
    }

    // 这里先这么些，感觉不太好，但是目前没想到其他的法子。
    private filterElement(groupElement: IGeometryElement[]): IGeometryElement[] {
        const filterPipe = [
            this.filterAreaLine,
        ];

        return filterPipe.reduce((curGroup, filter) => filter(curGroup), groupElement);
    }

    private filterLineMarker = (groupElement: IGeometryElement[]) => {
        const hasLineElement = groupElement.find(ele => isLineShape(ele.shapeType));
        const hasMarkerElement = groupElement.find(ele => this.isMarkerShape(ele.shapeType));

        if (hasLineElement && hasMarkerElement) {
            // CircleElement就是marker，当前不支持marker选中，选中无意义，先直接过滤掉
            return groupElement.filter(ele => !this.isMarkerShape(ele.shapeType));
        }

        return groupElement;
    };

    // 面积图在图形层面为line何area的组合，实际意义只有area，这里把line过滤
    private filterAreaLine = (groupElement: IGeometryElement[]) => {
        const hasLineElement = groupElement.find(ele => isLineShape(ele.shapeType));
        const hasAreaElment = groupElement.find(ele => this.isAreaShape(ele.shapeType));

        if (hasLineElement && hasAreaElment) {
            return groupElement.filter(ele => !isLineShape(ele.shapeType));
        }

        return groupElement;
    };
}


